import { useState } from "react";

interface TradeInput {
  pair: string;
  direction: "buy" | "sell";
  entryPrice: number;
  stopLossPrice: number;
  takeProfitPrice?: number;
  lotSize: number;
}

interface FormDataInput {
  challengeType: string;
  drawdownType: string;
  accountSize: number;
  startingBalance: number;
  currentBalance: number;
  leverage: number;
  dailyDrawdownPercent: number;
  maxDrawdownPercent: number;
  riskType: "percent" | "fixed";
  riskValue: number;
}

// Get pip increment, pip value per lot, and contract size for symbol
function getSymbolSpecs(pair: string) {
  try {
    const upper = pair.toUpperCase();
    if (upper.endsWith('JPY')) {
      return { 
        pipInc: 0.01, 
        pipValue: 9.30, // Adjusted for typical JPY pairs (value in USD per pip per standard lot)
        contractSize: 100000,
        isJPY: true 
      };
    }
    if (upper === 'XAUUSD' || upper === 'GOLD') {
      return { 
        pipInc: 0.01, 
        pipValue: 1, // Base pip value, will be adjusted based on current price
        contractSize: 100, // 100 oz per standard lot
        isGold: true // Flag to identify gold for special calculations
      };
    }
    if (upper === 'XAGUSD' || upper === 'SILVER') {
      return { pipInc: 0.01, pipValue: 5, contractSize: 5000 };
    }
    if (/^[A-Z]{6,7}$/.test(upper)) { // Standard forex
      return { pipInc: 0.0001, pipValue: 10, contractSize: 100000 };
    }
    // Default fallback, treat as unsupported
    return { pipInc: 0.0001, pipValue: 10, contractSize: 100000, unsupported: true };
  } catch (error) {
    console.error('Error in getSymbolSpecs:', error);
    return { pipInc: 0.0001, pipValue: 10, contractSize: 100000, unsupported: true };
  }
}


export const useLotCalculator = () => {
  const [result, setResult] = useState<any>(null);

  const calculate = (formData: FormDataInput, trades: TradeInput[]) => {
    try {
      const {
        accountSize,
        startingBalance,
        currentBalance,
        leverage,
        maxDrawdownPercent,
        challengeType,
        riskType,
        riskValue,
      } = formData;

      // Validation
      if (!accountSize || !startingBalance || !currentBalance || !leverage || !maxDrawdownPercent) {
        setResult({ error: 'Please fill in all required account fields.' });
        return;
      }
      if (accountSize <= 0 || startingBalance <= 0 || leverage <= 0) {
        setResult({ error: 'Account size, starting balance, and leverage must be positive.' });
        return;
      }

      let totalRiskValue = 0;
      let totalMarginNeeded = 0;
      let totalLots = 0;
      let tradeResults: any[] = [];
      let unsupportedSymbols: string[] = [];
      let tradeErrors: string[] = [];

      trades.forEach((trade, idx) => {
        try {
          const takeProfitPriceNum = trade.takeProfitPrice !== undefined ? Number(trade.takeProfitPrice) : 0;
          const entryPrice = Number(trade.entryPrice);
          const stopLossPrice = Number(trade.stopLossPrice);

          // Defensive: Check for NaN
          if (isNaN(entryPrice) || isNaN(stopLossPrice) || (trade.takeProfitPrice !== undefined && isNaN(takeProfitPriceNum))) {
            tradeErrors.push(`Trade #${idx + 1}: Invalid price input. Please enter valid numbers for entry, stop loss, and take profit.`);
            return;
          }

          // Basic trade validation
          if (!trade.pair || !trade.direction || entryPrice === undefined || stopLossPrice === undefined) {
            tradeErrors.push(`Trade #${idx + 1}: Missing required fields.`);
            return;
          }
          if (entryPrice <= 0 || stopLossPrice <= 0) {
            tradeErrors.push(`Trade #${idx + 1}: Prices must be positive.`);
            return;
          }
          if (entryPrice === stopLossPrice) {
            tradeErrors.push(`Trade #${idx + 1}: Entry and stop loss cannot be the same.`);
            return;
          }

          const { pipInc, pipValue, contractSize, unsupported, isJPY, isGold } = getSymbolSpecs(trade.pair);
          if (unsupported) unsupportedSymbols.push(trade.pair);

          // Calculate pip value based on instrument type
          let actualPipValue = pipValue;
          if (isGold) {
            // For gold, 1 pip = $0.1 per oz, so for 100 oz (1 lot) = $10 per pip
            actualPipValue = (contractSize * pipInc) * 10; // Corrected value for gold
          } else if (trade.pair.includes('XAG')) {
            // For silver, similar adjustment
            actualPipValue = (contractSize * pipInc) * 5;
          } else if (isJPY) {
            // For JPY pairs, we need to account for the correct pip value
            // This varies by the exchange rate, but we'll use a typical value
            // USDJPY at 108.00 has a pip value of about $9.30 per standard lot
            actualPipValue = 9.30; 
            
            // For crosses like EURJPY, we need a more dynamic calculation
            if (!trade.pair.startsWith('USD')) {
              // For non-USD pairs, adjust based on the base currency
              // This is a simplification - for production, you'd want to use real-time rates
              const baseCurrency = trade.pair.substring(0, 3);
              if (baseCurrency === 'EUR') actualPipValue = 10.63; // Higher for EUR
              else if (baseCurrency === 'GBP') actualPipValue = 12.50; // Higher for GBP
              else if (baseCurrency === 'AUD') actualPipValue = 7.20; // Lower for AUD
            }
          }

          // Positive pip distance depending on direction
          let pipDist = 0;
          if (trade.direction === "buy") {
            pipDist = (entryPrice - stopLossPrice) / pipInc;
          } else {
            pipDist = (stopLossPrice - entryPrice) / pipInc;
          }
          pipDist = Math.abs(pipDist);
          
          // Ensure pip distance makes sense for the instrument
          if (isJPY || isGold || trade.pair.includes('XAG')) {
            // For JPY pairs, gold and silver, adjust pip distance if needed
            pipDist = Math.round(pipDist * 100) / 100;
          }

          if (pipDist < 1) {
            tradeErrors.push(`Trade #${idx + 1}: Pip distance is too small (must be at least 1 pip). Please check your entry and stop loss.`);
            return;
          }

          // Precision risk management: calculate risk amount
          let riskAmount = 0;
          if (riskType === "percent") {
            riskAmount = (currentBalance * (riskValue / 100));
          } else {
            riskAmount = riskValue;
          }

          if (!riskAmount || riskAmount <= 0) {
            tradeErrors.push(`Trade #${idx + 1}: Risk amount must be positive.`);
            return;
          }

          // Calculate lot size for this trade
          let lotSize = riskAmount / (actualPipValue * pipDist);
          lotSize = isFinite(lotSize) && lotSize > 0 ? lotSize : 0;
          
          // Ensure lot size is not too small
          if (lotSize < 0.01) {
            lotSize = 0.01;
          }

          // Round lot sizes appropriately by instrument type
          if (isGold || trade.pair.includes('XAG')) {
            // For precious metals, round to 2 decimal places
            lotSize = Math.round(lotSize * 100) / 100;
          } else {
            // For forex pairs, round to 2 decimal places
            lotSize = Math.round(lotSize * 100) / 100;
          }

          totalLots += lotSize;
          // Calculate risk value more accurately based on instrument type
          let riskValueCalc = 0;
          if (isGold) {
            // For XAUUSD: risk = dollar move × lot size × contract size (ounces)
            riskValueCalc = Math.abs(entryPrice - stopLossPrice) * lotSize * contractSize;
          } else {
            // Standard calculation for forex and other pairs
            riskValueCalc = Math.abs(pipDist * lotSize * actualPipValue);
          }
          // Ensure risk value is accurate and rounded appropriately
          riskValueCalc = Math.round(riskValueCalc * 100) / 100;
          totalRiskValue += riskValueCalc;

          // Margin calculation - improved for accuracy
          let marginRequired = (contractSize * entryPrice * lotSize) / leverage;
          
          // For USD base currency pairs, calculation is different
          if (trade.pair.startsWith('USD')) {
            marginRequired = (contractSize * lotSize) / leverage;
          }
          
          // For JPY pairs, we need to handle margin differently
          if (isJPY) {
            // For JPY pairs, we divide by 100 to account for the different price scale
            marginRequired = (contractSize * (entryPrice/100) * lotSize) / leverage;
            
            // For USDJPY specifically
            if (trade.pair === 'USDJPY') {
              marginRequired = (contractSize * lotSize) / (entryPrice * leverage);
            }
          }
          
          // For special instruments like gold and silver
          if (isGold) {
            marginRequired = (contractSize * entryPrice * lotSize) / leverage;
          }
          totalMarginNeeded += marginRequired;

          let potentialProfit = 0;
          let riskToRewardRatio = 0;
          if (trade.takeProfitPrice !== undefined) {
            if (isGold) {
              potentialProfit = Math.abs(takeProfitPriceNum - entryPrice) * lotSize * contractSize;
            } else {
              const takeProfitPipDist = Math.abs((takeProfitPriceNum - entryPrice) / pipInc);
              potentialProfit = takeProfitPipDist * lotSize * actualPipValue;
            }
            riskToRewardRatio = riskValueCalc !== 0 ? potentialProfit / riskValueCalc : 0;
          }

          tradeResults.push({
            ...trade,
            pipInc,
            pipValue,
            contractSize,
            pipDist,
            lotSize,
            riskValue: riskValueCalc,
            marginRequired,
            potentialProfit,
            riskToRewardRatio,
            unsupported: !!unsupported,
            isJPY
          });
        } catch (error) {
          console.error(`Error processing trade #${idx + 1}:`, error);
          tradeErrors.push(`Trade #${idx + 1}: An error occurred during calculation.`);
        }
      });

      if (tradeErrors.length > 0) {
        setResult({ error: tradeErrors.join(' ') });
        return;
      }

      const totalRiskPercent = (totalRiskValue / accountSize) * 100;
      const currentDrawdownPercent = ((startingBalance - currentBalance) / startingBalance) * 100;

      const dailyRiskPercent = parseFloat(totalRiskPercent.toFixed(2));
      const marginWarning = totalMarginNeeded > accountSize;
      const drawdownWarning = currentDrawdownPercent >= maxDrawdownPercent;

      setResult({
        recommendedLotSize: totalLots.toFixed(2),
        riskPerTrade: totalRiskValue.toFixed(2),
        marginRequired: totalMarginNeeded.toFixed(2),
        dailyRiskPercent,
        currentDrawdownPercent: parseFloat(currentDrawdownPercent.toFixed(2)),
        maxDrawdownPercent,
        marginWarning,
        drawdownWarning,
        challengeType,
        trades: tradeResults,
        unsupportedSymbols,
        potentialProfit: tradeResults.length > 0 ? tradeResults[0].potentialProfit : "-",
        riskToRewardRatio: tradeResults.length > 0 ? tradeResults[0].riskToRewardRatio : "-",
        error: unsupportedSymbols.length > 0 ? `Warning: Some symbols may not be supported or use default pip/margin values: ${unsupportedSymbols.join(", ")}` : undefined
      });
    } catch (error) {
      console.error('Error in calculate:', error);
      setResult({ error: 'An error occurred during calculation. Please try again.' });
    }
  };

  return { result, calculate };
};

// --- Manual Test Log ---
// These are for developer validation only (not run in production)
function calculateRisk(symbol: string, lotSize: number, stopLossInPoints: number) {
  if (symbol === 'XAUUSD') {
    return lotSize * 100 * stopLossInPoints; // $100 per pip for 1.00 lot
  }
  if (symbol === 'EURUSD') {
    return lotSize * 10 * stopLossInPoints;
  }
  return 'Unknown symbol';
}
// XAUUSD Example
console.log('Test XAUUSD:', calculateRisk('XAUUSD', 0.03, 20)); // Expected: 60
// EURUSD Example
console.log('Test EURUSD:', calculateRisk('EURUSD', 0.10, 20)); // Expected: 20

export default useLotCalculator;
